// src/components/tasks/TaskDetail/TaskDetailContent.tsx
// 任務詳情內容區：標題、狀態標籤、描述

import React from 'react';
import { Task } from '../../../types/task';
import { CheckCircle, Repeat } from '../../common/icons';
import EditableText from '../../common/EditableText';

interface TaskDetailContentProps {
  task: Task;
  onUpdateTitle?: (title: string) => Promise<void>;
  onUpdateNotes?: (notes: string | null) => Promise<void>;
  initialEditMode?: boolean;
  forceCallOnBlur?: boolean;
}

const CLOSE_REASON_LABELS: Record<string, string> = {
  completed: 'Completed',
  split: 'Split into subtasks',
  scheduled: 'Scheduled in calendar',
  dropped: 'Dropped'
};

export const TaskDetailContent: React.FC<TaskDetailContentProps> = ({
  task,
  onUpdateTitle,
  onUpdateNotes,
  initialEditMode = false,
  forceCallOnBlur = false,
}) => {
  return (
    <div className="flex-1 md:min-w-0 flex flex-col">
      {/* Task Title */}
      <div className="mb-6 pl-4">
        {onUpdateTitle ? (
          <EditableText
            value={task.title}
            onSave={onUpdateTitle}
            placeholder="Enter task title..."
            maxLength={100}
            required
            className="text-xl font-semibold text-gray-800"
            inputClassName="text-xl font-semibold text-gray-800"
            initialEditMode={initialEditMode}
            forceCallOnBlur={forceCallOnBlur}
          />
        ) : (
          <h1 className="text-xl font-semibold text-gray-800 break-words">
            {task.title}
          </h1>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          {/* Status Badge */}
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
              ${task.status === 'OPEN'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
              }`}
          >
            <CheckCircle size={12} className="mr-1" />
            {task.status === 'OPEN' ? 'Open' : 'Closed'}
          </span>

          {/* Close Reason Badge */}
          {task.status === 'CLOSED' && task.closeReason && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-700 ml-2">
              {CLOSE_REASON_LABELS[task.closeReason] || task.closeReason}
            </span>
          )}

          {/* Migration Count Badge */}
          {task.migrationCount > 0 && (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                ${task.migrationCount >= 3
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-gray-100 text-gray-800'
                }`}
            >
              <Repeat size={12} className="mr-1" />
              Moved {task.migrationCount} {task.migrationCount === 1 ? 'time' : 'times'}
            </span>
          )}
        </div>
      </div>

      {/* Description (notes) */}
      <section className="mb-6 pl-4">
        {onUpdateNotes ? (
          <EditableText
            value={task.notes || ''}
            onSave={async (value) => {
              await onUpdateNotes(value || null);
            }}
            placeholder="Add notes..."
            maxLength={5000}
            multiline
            rows={4}
            minHeight="80px"
            className="text-gray-800"
            inputClassName="text-gray-800"
          />
        ) : (
          task.notes && (
            <p className="text-gray-800 whitespace-pre-line">{task.notes}</p>
          )
        )}
      </section>
    </div>
  );
};
